import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Sparkles, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { isNativeCapacitorApp } from "@/lib/runtime";

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/airport-chat`;
const FREE_MESSAGE_LIMIT = 5;
const STORAGE_KEY = "airport-buddy-chat-count";
const SIGNUP_BANNER_EVENT = "signup-banner-visibility";

type Msg = { role: "user" | "assistant"; content: string };

function getMessageCount(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return 0;
    const parsed = JSON.parse(stored);
    // Reset daily
    const today = new Date().toDateString();
    if (parsed.date !== today) return 0;
    return parsed.count || 0;
  } catch {
    return 0;
  }
}

function incrementMessageCount(): number {
  const today = new Date().toDateString();
  const current = getMessageCount();
  const next = current + 1;
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ date: today, count: next }));
  return next;
}

async function streamChat({
  messages,
  onDelta,
  onDone,
  onError,
}: {
  messages: Msg[];
  onDelta: (text: string) => void;
  onDone: () => void;
  onError: (err: string) => void;
}) {
  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify({ messages }),
  });

  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    onError(data.error || "Something went wrong. Please try again.");
    return;
  }

  if (!resp.body) {
    onError("No response received.");
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let newlineIndex: number;
    while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
      let line = buffer.slice(0, newlineIndex);
      buffer = buffer.slice(newlineIndex + 1);
      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;
      const jsonStr = line.slice(6).trim();
      if (jsonStr === "[DONE]") {
        onDone();
        return;
      }
      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content;
        if (content) onDelta(content);
      } catch {
        buffer = line + "\n" + buffer;
        break;
      }
    }
  }
  onDone();
}

interface ChatBotProps {
  /** When true, lifts the floating button above the iOS bottom tab bar */
  iosOffset?: boolean;
}

export const ChatBot = ({ iosOffset = false }: ChatBotProps = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSignupBannerVisible, setIsSignupBannerVisible] = useState(false);
  const [messageCount, setMessageCount] = useState(getMessageCount);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const navigate = useNavigate();

  const isLimitReached = messageCount >= FREE_MESSAGE_LIMIT;
  const remainingMessages = Math.max(0, FREE_MESSAGE_LIMIT - messageCount);

  useEffect(() => {
    const syncBannerVisibility = () => {
      setIsSignupBannerVisible(document.body.dataset.signupBannerVisible === "true");
    };
    const handleBannerVisibilityChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ isVisible?: boolean }>;
      setIsSignupBannerVisible(Boolean(customEvent.detail?.isVisible));
    };
    syncBannerVisibility();
    window.addEventListener(SIGNUP_BANNER_EVENT, handleBannerVisibilityChange as EventListener);
    return () => window.removeEventListener(SIGNUP_BANNER_EVENT, handleBannerVisibilityChange as EventListener);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const send = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading || isLimitReached) return;

    const userMsg: Msg = { role: "user", content: trimmed };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    const newCount = incrementMessageCount();
    setMessageCount(newCount);

    let assistantSoFar = "";
    const upsertAssistant = (chunk: string) => {
      assistantSoFar += chunk;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          return prev.map((m, i) =>
            i === prev.length - 1 ? { ...m, content: assistantSoFar } : m
          );
        }
        return [...prev, { role: "assistant", content: assistantSoFar }];
      });
    };

    try {
      await streamChat({
        messages: newMessages,
        onDelta: upsertAssistant,
        onDone: () => setIsLoading(false),
        onError: (err) => {
          toast({ variant: "destructive", title: "Chat Error", description: err });
          setIsLoading(false);
        },
      });
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to connect. Please try again." });
      setIsLoading(false);
    }
  }, [input, isLoading, isLimitReached, messages, toast]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const toggleButtonPosition = iosOffset
    ? "bottom-[calc(env(safe-area-inset-bottom,0px)+96px)]"
    : isSignupBannerVisible
      ? "bottom-28 sm:bottom-20"
      : "bottom-4";
  const chatWindowPosition = isMobile
    ? "inset-0 mx-auto sm:max-w-md"
    : isSignupBannerVisible
      ? "bottom-24 right-4"
      : "bottom-20 right-4";

  return (
    <>
      {isOpen && (
        <div
          className={`fixed z-50 animate-in slide-in-from-bottom-5 duration-300 ${chatWindowPosition}`}
          role="dialog"
          aria-label="Airport Buddy AI Chat"
          aria-modal="true"
        >
          <div className={cn(
            "relative flex flex-col border border-border bg-card shadow-2xl overflow-hidden",
            isMobile
              ? "h-[100dvh] w-full rounded-none"
              : "h-[600px] w-[400px] rounded-2xl"
          )}>
            {/* Header */}
            <div className={cn(
              "flex items-center justify-between px-4 py-3 border-b border-border bg-gradient-to-r from-primary/10 to-primary/5",
              isMobile && "safe-area-top"
            )}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Airport Buddy AI</p>
                  <p className="text-[11px] text-muted-foreground">
                    {remainingMessages > 0
                      ? `${remainingMessages} free message${remainingMessages !== 1 ? "s" : ""} left today`
                      : "Daily limit reached"}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center px-4 gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium text-foreground">Hi! I'm your Airport Buddy ✈️</p>
                  <p className="text-xs text-muted-foreground max-w-[260px]">
                    Ask me about TSA wait times, terminal tips, restaurants, or anything airport-related!
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2 justify-center">
                    {["TSA tips for JFK?", "Best food at ORD?", "How early should I arrive?"].map((q) => (
                      <button
                        key={q}
                        onClick={() => { setInput(q); }}
                        className="text-xs px-3 py-1.5 rounded-full border border-border bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex",
                    msg.role === "user" ? "justify-end" : "justify-start"
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm",
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : "bg-secondary/70 text-foreground rounded-bl-md"
                    )}
                  >
                    {msg.role === "assistant" ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                </div>
              ))}

              {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
                <div className="flex justify-start">
                  <div className="bg-secondary/70 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:0ms]" />
                      <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:150ms]" />
                      <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Limit reached banner */}
            {isLimitReached && (
              <div className={cn(
                "px-4 py-3 border-t border-border bg-primary/5",
                isMobile && "safe-area-bottom"
              )}>
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="h-4 w-4 text-primary flex-shrink-0" />
                  <p className="text-sm font-medium text-foreground">Daily limit reached</p>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Upgrade to Pro for unlimited AI chat, push alerts, and more.
                </p>
                <Button
                  size="sm"
                  className="w-full min-h-[40px]"
                  onClick={() => {
                    setIsOpen(false);
                    navigate(isNativeCapacitorApp() ? "/ios/pricing" : "/pricing");
                  }}
                >
                  Upgrade to Pro — $6.99/mo
                </Button>
              </div>
            )}

            {/* Input */}
            {!isLimitReached && (
              <div className={cn(
                "px-3 py-2.5 border-t border-border bg-card",
                isMobile && "safe-area-bottom"
              )}>
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about any airport..."
                    className="flex-1 resize-none rounded-xl border border-border bg-secondary/30 px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[40px] max-h-[100px]"
                    rows={1}
                    disabled={isLoading}
                  />
                  <Button
                    size="icon"
                    className="h-10 w-10 rounded-xl flex-shrink-0"
                    onClick={send}
                    disabled={!input.trim() || isLoading}
                    aria-label="Send message"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed right-4 z-50 h-14 w-14 min-h-[56px] min-w-[56px] rounded-full shadow-lg transition-all duration-200 hover:scale-110 focus-visible:ring-offset-4 ${toggleButtonPosition}`}
        size="icon"
        aria-label={isOpen ? "Close chat assistant" : "Open chat assistant"}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </Button>
    </>
  );
};
